import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import Button from '../../components/ui/Button';
import { playerProfileService, clubCareerService, transferHistoryService, performanceStatsService, playerMediaService } from '../../services/playerMasterService';
import HeroSection from './components/HeroSection';
import MediaGallery from './components/MediaGallery';
import CareerTimeline from './components/CareerTimeline';
import TransferHistory from './components/TransferHistory';
import PerformanceMetrics from './components/PerformanceMetrics';
import ContactInquiry from './components/ContactInquiry';

// UUID validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PublicPlayerPortfolio = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [careerHistory, setCareerHistory] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [performanceStats, setPerformanceStats] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (playerId) {
      // Validate UUID format
      if (!UUID_REGEX?.test(playerId)) {
        setError('Invalid player ID format. Please use a valid player portfolio link.');
        setLoading(false);
        return;
      }
      fetchPlayerPortfolio();
    }
  }, [playerId]);

  const fetchPlayerPortfolio = async () => {
    setLoading(true);
    setError(null);
    try {
      const [playerRes, careerRes, transferRes, statsRes, mediaRes] = await Promise.all([
        playerProfileService?.getById(playerId),
        clubCareerService?.getByPlayerId(playerId),
        transferHistoryService?.getByPlayerId(playerId),
        performanceStatsService?.getByPlayerId(playerId),
        playerMediaService?.getByPlayerId(playerId)
      ]);
      
      setPlayer(playerRes?.data);
      setCareerHistory(careerRes?.data || []);
      setTransferHistory(transferRes?.data || []);
      setPerformanceStats(statsRes?.data || []);
      setMedia(mediaRes?.data || []);
    } catch (err) {
      console.error('Error fetching player portfolio:', err);
      setError('Failed to load player portfolio. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading player portfolio...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Player Not Found</h2>
            <p className="text-muted-foreground mb-6">{error || 'The requested player portfolio could not be found.'}</p>
            <Button onClick={() => navigate('/')}>Return to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <HeroSection player={player} />
        
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 space-y-16">
          <MediaGallery media={media} playerName={player?.fullName} />
          
          <PerformanceMetrics stats={performanceStats} player={player} />
          
          <CareerTimeline careerHistory={careerHistory} />
          
          <TransferHistory transferHistory={transferHistory} />
          
          <ContactInquiry player={player} />
        </div>
      </main>
      
      <footer className="bg-card border-t border-border py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center text-muted-foreground">
          <p>© 2026 ProX11Market. All rights reserved. Professional player portfolio for recruitment purposes.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicPlayerPortfolio;